import type { GuidanceAnchor } from '@ziro/legal-shared-types';

/**
 * A guidance anchor with its provenance ALREADY RESOLVED by the caller.
 *
 * `GuidanceAnchor.sourceIds` are opaque ids. Rendering "2 source(s)" is a
 * number, not an answer: spec decision D4 asks each rail entry to cite ITS
 * sources, so a caseworker can answer "why does this clause say that?" —
 * which the retired `<sup>` markers did by resolving `data-sources` against
 * `guidanceBrief.sources`.
 *
 * Resolving them is a CALLER job, not a viewer one. The viewer resolves
 * nothing (decision D2) — it has no idea what a checkpoint or a guidance
 * brief is, and giving it one would be the first branch-on-caller that stops
 * a "universal" viewer being universal. So the caller that HAS the brief
 * resolves the ids and passes the labels down. Both fields are optional and
 * the rail degrades to the bare count when they are absent.
 */
export interface ViewerGuidanceAnchor extends GuidanceAnchor {
  /** Display names for `sourceIds`, in order. */
  sourceLabels?: string[];
  /** The guidance points citing those sources — the "why" behind the wording. */
  sourcePoints?: string[];
}

/** Caller-supplied toolbar action — rendered by the viewer toolbar (Library and Catalogue consolidation). */
export interface ViewerAction {
  id: string;
  label: string;
  intent?: 'primary' | 'default';
  onClick: () => void | Promise<void>;
  disabledReason?: string;
  busy?: boolean;
}

/**
 * Everything the viewer needs, resolved by the CALLER.
 *
 * The viewer never derives a URL, never knows about cases, templates,
 * checkpoints or research documents. Four unrelated surfaces open it, each with
 * its own fetch-and-auth path; branching on caller inside the component is how a
 * "universal" viewer stops being universal.
 */
export interface ViewerSource {
  /** Shown in the panel header. */
  title: string;
  /** ALWAYS a PDF. A DOCX is converted server-side before it reaches here. */
  pdfUrl: string;
  /** Selects the action button. Does not affect rendering. */
  originalKind: 'DOCX' | 'PDF';
  /** DOCX only — OneDrive working copy, opened in a new tab. */
  wordWebUrl?: string;
  /**
   * DOCX only — opens the document in Word when the target is not a URL the
   * caller can hand over up front.
   *
   * A workflow doc-gen step cannot fill `wordWebUrl`: its Word target is
   * minted by an async call that creates an edit session, so there is no URL
   * to pass at open time. With only `wordWebUrl` to express intent, that
   * caller passed nothing, and the toolbar fell through to its "no Word copy"
   * fallback — "Open in new tab", pointing at the RENDERED PDF. A DOCX step
   * therefore offered no way into Word at all (reported 2026-09-03).
   *
   * Callers supply whichever they have: a URL when one exists, this when
   * getting one requires work. The viewer still resolves nothing itself
   * (design decision D2) — it just invokes what it was handed.
   */
  onOpenInWord?: () => void | Promise<void>;
  /**
   * DOCX only — the Word action is shown but disabled, with this as its
   * tooltip (e.g. the document is being rebuilt: an edit made now would be
   * overwritten when the job writes the new version). Wins over
   * `wordWebUrl` / `onOpenInWord`.
   */
  openInWordDisabledReason?: string;
  /** The ORIGINAL format, never the rendered PDF. */
  downloadUrl: string;
  /** Change this to force a re-render (see the auto-update task). */
  refreshToken?: string | number;
  /**
   * Stable identity of the document, when it is not the URL (theme styling
   * Part 2, ruling Q6). A caller whose every revision lives at a NEW URL — the
   * theme editor's specimen drafts, one tokenised URL per draft — sets this so
   * a new URL reads as a refresh of the same document (page, zoom and finder
   * kept; a failed load falls back to the last good render) rather than as a
   * different document. Absent → `pdfUrl` is the identity, exactly as before.
   */
  documentKey?: string;
  /**
   * Called when a load of `pdfUrl` fails (theme styling Part 4b). `status` is
   * the HTTP status when the server answered, absent for a network failure
   * or unparseable bytes. The viewer still shows its own error / last-good
   * fallback; this only lets the caller react — e.g. the theme editor
   * re-mints an expired specimen draft token (404) and re-opens. Absent →
   * nothing changes.
   */
  onLoadError?: (info: { pdfUrl: string; documentKey?: string; status?: number }) => void;
  /**
   * The caller's words for a failed load (AI-fill Task 15), in place of the
   * viewer's generic "could not be rendered". Only the caller knows what its
   * route's refusals mean — a case document's preview answers 422 while
   * review tags remain, which no amount of retrying fixes. May be async (the
   * caller can re-request the URL to read the JSON refusal pdf.js never
   * exposes). Resolve null to keep the generic message. Absent → unchanged.
   */
  describeLoadError?: (info: { status?: number }) => string | null | Promise<string | null>;
  /** Optional guidance rail. The caller supplies them; the viewer draws them. */
  guidanceAnchors?: ViewerGuidanceAnchor[];
  /**
   * Show the highlight key above the document.
   *
   * The colours are baked into the rendered PDF by the caller's own preview
   * route (the templates route shades detected fields green and named sections
   * purple), so only that caller knows whether a key applies — the viewer
   * cannot tell shaded bytes from unshaded ones. Which is why this is a
   * caller-supplied flag rather than something the viewer infers.
   */
  legend?: 'fields' | 'guidance';
  /**
   * Caller-supplied actions (Library and Catalogue consolidation, 2026-10-04).
   * The viewer still resolves nothing: the caller decides which actions the
   * current user may take on THIS document and what they do. Rendered as a
   * row under the page navigation; absent → nothing rendered. The case
   * callers pass none and are unchanged.
   */
  actions?: ViewerAction[];
}

/** Zoom steps, as a multiple of the fit-to-width size. 1 is "Fit". */
export const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 2] as const;
